// Проверка полей при вводе.
//
// ЗАЧЕМ. Каждая проверка здесь закрывает ошибку, которая в зеркале уже случилась и стоила
// разбора: IMO, равный собственному MMSI судна; ИНН из девяти цифр; флаг «Либерия»,
// набранный в латинской раскладке; сумма, записанная строкой «515000|RUB». База такие
// значения не примет (в своей схеме на них стоят CHECK), но узнать об этом на кнопке
// «Сохранить» — значит потерять всё, что человек набрал до неё.
//
// ЧТО ЗДЕСЬ НЕ ДЕЛАЕТСЯ. Проверка не ходит в ядро: ни «такой ИНН уже заведён», ни «номер
// занят». Единственность — дело базы, и обещать её на форме нельзя. Здесь только то, что
// проверяется по самому значению.

import { вариантыПоля, пусто, число, этоДеньги, этоЯрлык } from './values'
import type { Значение, Значения, Поле } from './types'

// ─────────────────────────── готовые проверки ───────────────────────────

/** ИНН: десять цифр у юрлица, двенадцать у предпринимателя. */
export const проверкаИНН = (v: Значение): string | null => {
  if (пусто(v)) return null
  const s = String(v).trim()
  return /^[0-9]{10}([0-9]{2})?$/.test(s) ? null : 'ИНН — десять цифр у юрлица или двенадцать у ИП'
}

/** КПП: девять знаков. */
export const проверкаКПП = (v: Значение): string | null => {
  if (пусто(v)) return null
  return /^[0-9A-Z]{9}$/.test(String(v).trim()) ? null : 'КПП — девять знаков'
}

/**
 * Контрольная сумма IMO. Шесть первых цифр умножаются на веса от семи до двух,
 * последняя цифра суммы — седьмая цифра номера.
 */
export function контрольнаяIMO(s: string): boolean {
  if (!/^[0-9]{7}$/.test(s)) return false
  const веса = [7, 6, 5, 4, 3, 2]
  let сумма = 0
  for (let i = 0; i < веса.length; i += 1) сумма += Number(s[i]) * веса[i]
  return сумма % 10 === Number(s[6])
}

export const проверкаIMO = (v: Значение): string | null => {
  if (пусто(v)) return null
  const s = String(v).trim()
  if (!/^[0-9]{7}$/.test(s)) return 'IMO — ровно семь цифр'
  if (!контрольнаяIMO(s)) return 'IMO не сходится по контрольной цифре — проверьте номер'
  return null
}

export const проверкаMMSI = (v: Значение): string | null => {
  if (пусто(v)) return null
  return /^[0-9]{9}$/.test(String(v).trim()) ? null : 'MMSI — ровно девять цифр'
}

export const проверкаПочты = (v: Значение): string | null => {
  if (пусто(v)) return null
  const s = String(v).trim()
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s) ? null : 'Почта без «собаки» или без домена'
}

export const проверкаТелефона = (v: Значение): string | null => {
  if (пусто(v)) return null
  const цифры = String(v).replace(/\D/g, '')
  return цифры.length >= 10 ? null : 'Телефон короче десяти цифр'
}

/** Дата в виде «ГГГГ-ММ-ДД». Всё остальное — не дата, а строка, похожая на дату. */
export const разобранаДата = (s: string): boolean => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim())
  if (!m) return false
  const d = new Date(`${s.trim()}T12:00:00Z`)
  if (Number.isNaN(d.getTime())) return false
  return d.getUTCMonth() + 1 === Number(m[2]) && d.getUTCDate() === Number(m[3])
}

// ─────────────────────────── проверка поля ───────────────────────────

/** Обязательно ли поле при текущих значениях. */
export function обязательно(поле: Поле, все: Значения): boolean {
  const о = поле.обязательно
  return typeof о === 'function' ? о(все) : Boolean(о)
}

/** Можно ли поле править при текущих значениях. */
export function толькоЧтение(поле: Поле, все: Значения): boolean {
  const т = поле.толькоЧтение
  return typeof т === 'function' ? т(все) : Boolean(т)
}

/**
 * Ошибка поля или null.
 *
 * Порядок важен: сначала обязательность (пустое обязательное — самая частая причина),
 * потом вид, потом своя проверка. Иначе на пустом поле сработала бы проверка формата
 * и человек прочёл бы «IMO — ровно семь цифр» там, где надо «заполните».
 */
export function проверитьПоле(поле: Поле, все: Значения): string | null {
  const v = все[поле.код]
  if (толькоЧтение(поле, все)) return null

  if (пусто(v)) {
    if (!обязательно(поле, все)) return null
    return поле.почемуОбязательно
      ? `Заполните: ${поле.почемуОбязательно}`
      : 'Обязательное поле'
  }

  switch (поле.вид) {
    case 'число': {
      if (число(все, поле.код) === null) return 'Нужно число'
      break
    }
    case 'деньги': {
      if (!этоДеньги(v)) return 'Нужна сумма'
      if (v.сумма !== null && !Number.isFinite(v.сумма)) return 'Нужна сумма'
      if (!/^[A-Z]{3}$/.test(v.валюта)) return 'Валюта — трёхбуквенный код'
      break
    }
    case 'дата': {
      if (!разобранаДата(String(v))) return 'Дата не разобрана: нужен вид ГГГГ-ММ-ДД'
      break
    }
    case 'список': {
      // Набор спрашивается через вариантыПоля, а не берётся плоским: стадия чужой воронки
      // есть в справочнике `stage`, но в ЭТОЙ сделке она не значит ничего.
      const набор = вариантыПоля(поле, все)
      const коды = набор.map((x) => x.код)
      if (коды.length && !коды.includes(String(v))) {
        return поле.вариантыПо && набор !== поле.варианты
          ? 'Стадия из другой воронки: выберите из списка этой'
          : 'Значения нет в справочнике'
      }
      break
    }
    case 'связь': {
      if (!этоЯрлык(v) || !v.id) return 'Выберите карточку из поиска'
      break
    }
    case 'строка':
    case 'текст': {
      const s = String(v)
      if (поле.максДлина && s.length > поле.максДлина) {
        return `Длиннее допустимого: ${s.length} против ${поле.максДлина}`
      }
      break
    }
    default:
      break
  }

  return поле.проверка ? поле.проверка(v, все) : null
}

/** Все ошибки карточки: код поля → текст. Пустой объект значит «можно сохранять». */
export function проверитьКарточку(поля: Поле[], все: Значения): Record<string, string> {
  const ошибки: Record<string, string> = {}
  for (const п of поля) {
    const е = проверитьПоле(п, все)
    if (е) ошибки[п.код] = е
  }
  return ошибки
}

/**
 * Незаполненные обязательные поля — списком, для шапки карточки.
 *
 * Отдельно от ошибок нарочно: «осталось заполнить три поля» человек читает до того, как
 * дошёл до них, а красное «Обязательное поле» видно только рядом с самим полем.
 */
export function незаполненныеОбязательные(поля: Поле[], все: Значения): Поле[] {
  return поля.filter((п) => !толькоЧтение(п, все) && обязательно(п, все) && пусто(все[п.код]))
}
